import {
  DioboxEvent, WhatsAppTemplate, WhatsAppTemplateChannel, WhatsAppTemplateVariable,
} from "sharedTypes"
import { MERGE_TAGS } from "utilities/enums"

export const EDITABLE_FIELD_CHARACTER_LIMIT = 200

export const joinPunctuation = (content: string, index: number) => (
  index > 0 && /^[.,!?:;]/.test(content) ? `\u2060${content}` : content
)

// WhatsApp template headers only support JPEG and PNG.
// Handles URLs with query strings (e.g. signed URLs from upload APIs)
// and QR code API URLs with format=png parameter.
export const isImageUrl = (url: string) => (
  /\.(jpe?g|png)(\?|$)/i.test(url) || /[?&]format=png(&|$)/i.test(url)
)

export type TemplateFilters = {
  channel: WhatsAppTemplateChannel | "all"
  showUtility: boolean
  showMarketing: boolean
  messageType: string
  language: string
}

export type TemplatePreviewSegment =
  | { type: "text", content: string }
  | { type: "variable", index: number, variable: WhatsAppTemplate["variables"][number] }

export const MESSAGE_TYPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "opt_in", label: "Opt-In" },
  { value: "invitation", label: "Invitations" },
  { value: "rsvp_reminder", label: "RSVP Reminders" },
  { value: "registration_reminder", label: "Registration Reminders" },
  { value: "data_collection", label: "Data Collection" },
  { value: "event_update", label: "Event Updates" },
  { value: "confirmation", label: "Confirmations" },
  { value: "qr_code", label: "QR Code Distribution" },
]

export const LANGUAGE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "en", label: "English" },
  { value: "fr", label: "French" },
  { value: "de", label: "German" },
]

export const DEFAULT_FILTERS: TemplateFilters = {
  channel: "all",
  showUtility: true,
  showMarketing: true,
  messageType: "all",
  language: "all",
}

export const filterTemplates = (
  templates: WhatsAppTemplate[],
  filters: TemplateFilters,
): WhatsAppTemplate[] => templates.filter((template) => {
  if (filters.channel !== "all") {
    if (!template.channels.includes(filters.channel)) return false
  }
  if (filters.channel === "whatsapp" || filters.channel === "all") {
    if (!filters.showUtility && template.category === "utility") return false
    if (!filters.showMarketing && template.category === "marketing") return false
  }
  if (filters.messageType !== "all" && template.messageType !== filters.messageType) return false
  if (filters.language !== "all" && template.language !== filters.language) return false

  return true
})

export const parseTemplatePreview = (
  preview: string,
  variables: WhatsAppTemplate["variables"],
): TemplatePreviewSegment[] => {
  const segments: TemplatePreviewSegment[] = []
  const regex = /\{\{(\d+)\}\}/g
  let lastIndex = 0
  let match = regex.exec(preview)

  while (match !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: "text", content: preview.slice(lastIndex, match.index) })
    }

    const variableIndex = parseInt(match[1], 10) - 1
    const variable = variables[variableIndex]

    if (variable) {
      segments.push({ type: "variable", index: variableIndex, variable })
    } else {
      segments.push({ type: "text", content: match[0] })
    }

    lastIndex = match.index + match[0].length
    match = regex.exec(preview)
  }

  if (lastIndex < preview.length) {
    segments.push({ type: "text", content: preview.slice(lastIndex) })
  }

  return segments
}

/**
 * Build a complete variable set (locked + user-controlled) for preview rendering.
 * Locked variables are resolved via the provided resolver function.
 * User-controlled variables are taken from whatsappContentVariables.
 */
export const buildResolvedVariables = (
  variables: WhatsAppTemplateVariable[],
  userVariables: Record<string, string>,
  resolveLockedVariable: (mergeTag: string) => string,
): Record<string, string> => {
  const result: Record<string, string> = {}
  variables.forEach((variable, index) => {
    const key = (index + 1).toString()
    if (variable.type === "locked" && variable.mergeTag) {
      result[key] = resolveLockedVariable(`{{${variable.mergeTag}}}`)
    } else {
      // User values may contain merge tags (e.g., {{FIRST_NAME}} from name field selection)
      // Run them through the resolver so previews show actual guest data
      const value = userVariables[key] ?? ""
      result[key] = value ? resolveLockedVariable(value) : ""
    }
  })

  return result
}

/**
 * Render a template preview string by substituting positional variables.
 */
export const renderTemplatePreview = (
  preview: string,
  variables: Record<string, string>,
): string => preview.replace(
  /\{\{(\d+)\}\}/g,
  (match, index: string) => variables[index] ?? match,
)

/**
 * Build a freeform SMS body from a template.
 * Locked variables become merge tags (e.g. {{FIRST_NAME}}).
 * User-controlled variables with values use the stored value.
 * User-controlled variables without values use the variable label as placeholder.
 */
export const buildFreeformBody = (
  template: WhatsAppTemplate,
  userVariables: Record<string, string>,
): string => {
  const freeformVariables: Record<string, string> = {}
  template.variables.forEach((variable, index) => {
    const key = (index + 1).toString()
    if (variable.type === "locked" && variable.mergeTag) {
      freeformVariables[key] = `{{${variable.mergeTag}}}`
    } else if (variable.source === "forms" || variable.type === "selectable") {
      freeformVariables[key] = userVariables[key] || "{{FORM_}}"
    } else if (variable.source === "name_fields") {
      freeformVariables[key] = userVariables[key] || "{{FIRST_NAME}}"
    } else {
      freeformVariables[key] = userVariables[key] || variable.label.toUpperCase()
    }
  })

  return renderTemplatePreview(template.preview, freeformVariables)
}

/**
 * Build pre-populated variable values for a new text message.
 * Source-based variables keep merge tag references.
 * Editable variables resolve to actual event data.
 */
// True iff every non-locked variable in the template has a non-empty value in
// the TextMessage's `whatsappContentVariables`. Template-aware so that missing
// keys count as empty — `buildInitialVariables` only seeds keys with defaults,
// so a freshly-created TextMessage's persisted record can be missing keys for
// form-link / name-field / media variables until the user touches them.
export const hasVariableValue = (value: unknown): boolean => (
  typeof value === "string" && value.trim().length > 0
)

export const isWhatsappTextMessageConfigured = (
  textMessage: { whatsappContentVariables?: Record<string, string> } | null | undefined,
  template: WhatsAppTemplate | null | undefined,
): boolean => {
  if (!template) return false
  const variables = textMessage?.whatsappContentVariables ?? {}

  return template.variables.every((variable, index) => {
    if (variable.type === "locked") return true

    return hasVariableValue(variables[(index + 1).toString()])
  })
}

// Collect labels of non-locked, non-media variables whose value is empty.
// Media variables are skipped because each attachment renders its own
// "Not uploaded" warning next to the upload control.
export const getMissingBodyVariableLabels = (
  textMessage: { whatsappContentVariables?: Record<string, string> } | null | undefined,
  template: WhatsAppTemplate | null | undefined,
): string[] => {
  if (!template) return []
  const variables = textMessage?.whatsappContentVariables ?? {}
  const labels: string[] = []

  template.variables.forEach((variable, index) => {
    if (variable.type === "locked" || variable.type === "media") return
    if (hasVariableValue(variables[(index + 1).toString()])) return
    labels.push(variable.label)
  })

  return labels
}

export const buildInitialVariables = (
  template: WhatsAppTemplate,
  event: DioboxEvent,
): Record<string, string> => {
  const result: Record<string, string> = {}

  template.variables.forEach((variable, index) => {
    const key = (index + 1).toString()
    if (variable.type !== "user" || !variable.defaultValue) return

    if (variable.source) {
      // Source-based (name_fields, forms): keep as merge tag reference
      result[key] = variable.defaultValue
    } else {
      // Editable: resolve merge tags to actual values
      result[key] = variable.defaultValue
        .replace("{{EVENT_NAME}}", event.publicEventTitle || event.title)
    }
  })

  return result
}

export const getTemplateMediaUrl = (
  template: WhatsAppTemplate,
  contentVariables?: Record<string, string>,
  resolvedVariables?: Record<string, string>,
): string | undefined => {
  const mediaIndex = template.variables.findIndex((v) => v.type === "media")
  if (mediaIndex !== -1) {
    return contentVariables?.[(mediaIndex + 1).toString()] || undefined
  }

  const qrIndex = template.variables.findIndex((v) => v.mergeTag === "GUEST_QR_CODE_IMAGE")
  if (qrIndex !== -1) {
    return resolvedVariables?.[(qrIndex + 1).toString()] || undefined
  }

  return undefined
}

export const hasQRCodeConflict = (
  template: WhatsAppTemplate | null,
  expiringQrCodes: boolean,
): boolean => {
  if (!template || !expiringQrCodes) return false

  return template.variables.some((v) => v.mergeTag === "GUEST_QR_CODE_IMAGE")
}

/**
 * Resolve a stored merge tag reference to its display label.
 * e.g., "{{FULL_NAME}}" → "Full Name", "{{FORM_abc}}" → "{{FORM_abc}}" (unchanged)
 * Plain text values pass through unchanged.
 */
export const resolveMergeTagLabel = (value: string): string => {
  const match = value.match(/^\{\{(\w+)\}\}$/)
  if (!match) return value

  const tag = MERGE_TAGS.find((t) => t.id === match[1])

  return tag ? tag.label : value
}
